import type { Marketplace } from '@amzn/journeys-common';
import { isNumber } from 'es-toolkit/compat';
import { type AsinLaunchGating, AsinLaunchGatingAttributeEnum, type DpAttribute } from 'generated/graphql';
import { getLaunchDateAndCurrentDate } from './get-launch-date-and-current-date';
import { DEFAULT_LAUNCH_DATE_WINDOW_HOURS, MARKETPLACE_LAUNCH_DATE_WINDOW_HOURS } from './launch-date-windows';

/**
 * Gets launch gating information for this ASIN based on announce date and (legacy) PSLD logic.
 * @param attributes
 * @param marketplace Marketplace code (e.g. 'US', 'MX') or an array of Marketplace codes. If multiple marketplaces are provided, the minimum window will be returned.
 * @return True if the launch date exists and is within the launch date window. False
 *         otherwise.
 */
export function withinLaunchDateWindow({
  marketplace,
  attributes,
  launchGating,
}: {
  attributes: Pick<DpAttribute, 'key' | 'value'>[] | undefined;
  marketplace: Marketplace | Marketplace[];
  launchGating?: AsinLaunchGating;
}): AsinLaunchGating {
  // ASIN exists in DCS, use new backend logic
  if (launchGating) return launchGating;

  // ASIN only exists in DPB, use legacy (client-side) fallback logic
  const launchDateThreshold = getLaunchDateWindow(marketplace);
  const [launchDate, now] = getLaunchDateAndCurrentDate(attributes);
  const attributeChecked = AsinLaunchGatingAttributeEnum.ProductSiteLaunchDate;
  return {
    attributeChecked,
    beyondLaunchDate: false,
    launchDateThreshold,
    withinLaunchDate: launchDate?.isBefore(now.add(launchDateThreshold, 'hours')) ?? false,
  };
}

export function getLaunchDateWindow(marketplace: Marketplace | Marketplace[]): number {
  const mpsArray = Array.isArray(marketplace)
    ? marketplace
    : [
        marketplace,
      ];
  const mpLaunchWindows = mpsArray.map((mp) => MARKETPLACE_LAUNCH_DATE_WINDOW_HOURS[mp]).filter((win) => isNumber(win)) as Array<number>;
  const minMPWindow = mpLaunchWindows.length > 0 ? Math.min(...mpLaunchWindows) : undefined;
  return minMPWindow ?? DEFAULT_LAUNCH_DATE_WINDOW_HOURS;
}

export function getLaunchDateWindowForMarketplace(marketplace: Marketplace): number {
  const window = MARKETPLACE_LAUNCH_DATE_WINDOW_HOURS[marketplace];
  return isNumber(window) ? window : DEFAULT_LAUNCH_DATE_WINDOW_HOURS;
}
